import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCredential, getCredentials } from '../../../../api/endpoints/system';
import type { Credential } from '../../../../api/types/system';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../components/ListSearchField';
import { CredentialFormDialog } from '../CredentialFormDialog';

/** Masks a sensitive value for list display — never show it in full, just enough to
 *  distinguish rows (last 2 chars) when present. */
function maskSecret(value?: string | null): string {
  if (!value) return '—';
  const tail = value.slice(-2);
  return `••••••${tail}`;
}

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/system.ts header). */
export default function CredentialListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Credential | null>(null);
  const [deleting, setDeleting] = useState<Credential | null>(null);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['system', 'credentials'], queryFn: () => getCredentials() });
  const rows: Credential[] = useMemo(() => query.data?.credentials ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.provider, row.providerName, row.providerType, row.serviceUsername, row.gsaId, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCredential(id),
    onSuccess: () => {
      enqueueSnackbar('Provider ayarı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'credentials'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Provider Ayarları</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Provider Ayarı
        </Button>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Sağlayıcı, tip, kullanıcı adı..." />
      </Box>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sağlayıcı</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Servis Kullanıcı Adı</TableCell>
              <TableCell>Servis Şifresi</TableCell>
              <TableCell>Firma ID</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı provider ayarı bulunamadı.' : 'Aramayla eşleşen provider ayarı bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.provider ?? row.providerName ?? '—'}</TableCell>
                <TableCell>{row.providerType ?? '—'}</TableCell>
                <TableCell>{row.serviceUsername ?? '—'}</TableCell>
                <TableCell>{maskSecret(row.servicePassword)}</TableCell>
                <TableCell>{row.gsaId ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.isActive ? 'Aktif' : 'Pasif'}
                    color={row.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CredentialFormDialog open={createOpen} credential={null} onClose={() => setCreateOpen(false)} />
      <CredentialFormDialog open={!!editing} credential={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Provider ayarını sil"
        description={`"${deleting?.provider ?? ''}" provider ayarını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
